import React, { useState, useEffect, useMemo } from "react";
import { Copy, Check } from "lucide-react";
import ModalModel from "./ModalModel";
import ButtonCta from "../Buttons/ButtonCta";
import Login from "../../pages/auth/Login";
import AuthService from "../../services/authService";
import shareLinkService from "../../services/shareLinkService";
import {
  buildWhatsAppShareText,
  detectProductShareKind,
  resolveShareCopy,
} from "../../constants/contact";
import { getImageUrl } from "../../services";
import { resolveProductImagePath } from "../../utils/variants";
import { parsePrice } from "../../utils/productPrice";
import {
  FacebookIcon,
  InstagramIcon,
  MessengerIcon,
  WhatsAppIcon,
} from "../icons/SocialBrandIcons";

/**
 * @param {object} props
 * @param {boolean} [props.isProduct=true]
 * @param {'app'|'business'|'standard'|'sample'} [props.shareKind] — force le type de texte
 * @param {{ budget?: number, pieces?: number, profit?: number, unitPrice?: number }} [props.businessOffer]
 */
export default function ShareProduct({
  toggleActiveShare,
  activeShare,
  product = null,
  isProduct = true,
  shareKind: shareKindProp,
  businessOffer = null,
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(!!AuthService.getToken?.());
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!AuthService.getToken?.());
  }, []);

  const toggleModal = () => {
    try {
      toggleActiveShare();
    } catch {}
  };

  const handleLoginSuccess = () => {
    setIsLoggedIn(!!AuthService.getToken?.());
    setShowLoginModal(false);
  };

  const shareKind = useMemo(() => {
    if (shareKindProp) return shareKindProp;
    if (!isProduct) return "app";
    if (businessOffer) return "business";
    return detectProductShareKind(product);
  }, [shareKindProp, isProduct, businessOffer, product]);

  const shareCopy = useMemo(() => {
    const price =
      Number(product?.prix ?? product?.price ?? product?.sale_price ?? 0) || 0;
    return resolveShareCopy(shareKind, {
      price,
      budget: businessOffer?.budget,
      pieces: businessOffer?.pieces,
      profit: businessOffer?.profit,
      unitPrice: businessOffer?.unitPrice,
    });
  }, [shareKind, product, businessOffer]);

  if (!activeShare) return null;

  return (
    <ModalModel onClose={toggleModal} title={shareCopy.modalTitle} active={true}>
      <ShareContent
        isProduct={isProduct}
        isLoggedIn={isLoggedIn}
        onRequestLogin={() => setShowLoginModal(true)}
        product={product}
        shareCopy={shareCopy}
      />

      {showLoginModal && (
        <ModalModel onClose={() => setShowLoginModal(false)} title="Connexion" active={true}>
          <Login onLoginSuccess={handleLoginSuccess} />
        </ModalModel>
      )}
    </ModalModel>
  );
}

/** Ouvre Instagram (app si possible) + feuille de partage native si dispo */
const shareToInstagram = async (url, text) => {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({
        text,
        url,
      });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  }

  const appLink = "instagram://app";
  const webLink = "https://www.instagram.com/";
  const opened = window.open(appLink, "_blank");
  if (!opened) {
    window.location.href = appLink;
  }
  setTimeout(() => {
    if (document.hidden) return;
    window.open(webLink, "_blank", "noopener,noreferrer");
  }, 700);
};

function ShareContent({ isProduct, isLoggedIn, onRequestLogin, product, shareCopy }) {
  const [shareUrl, setShareUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);

  const appShareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/`
      : "https://chinexpress.com/";

  useEffect(() => {
    if (!isProduct) {
      setShareUrl(appShareUrl);
      setLoading(false);
      setError(null);
      return;
    }
    if (!isLoggedIn || !product?.id || shareUrl) return;
    setLoading(true);
    shareLinkService
      .generateShareLink(product.id)
      .then((data) => {
        const url = shareLinkService.buildFrontShareUrl(data.affiliate_link);
        setShareUrl(url);
      })
      .catch(() => setError("Impossible de générer le lien"))
      .finally(() => setLoading(false));
  }, [isLoggedIn, product?.id, shareUrl, isProduct, appShareUrl]);

  const handleShare = async (key) => {
    if (isProduct && !isLoggedIn) {
      onRequestLogin();
      return;
    }
    if (!shareUrl) return;

    if (key === "Instagram") {
      await shareToInstagram(shareUrl, shareCopy.whatsappPrefill);
      return;
    }
    if (key === "WhatsApp") {
      // Corps uniquement (pas de titre) + CTA déjà dans whatsappPrefill avec saut de ligne
      const text = buildWhatsAppShareText(shareCopy.whatsappPrefill, shareUrl);
      window.open(
        `https://wa.me/?text=${encodeURIComponent(text)}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (key === "Facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (key === "Messenger") {
      window.open(
        `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        Object.assign(ta.style, { position: "fixed", opacity: "0", top: "0", left: "0" });
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch {
      setError("Erreur lors de la copie");
    }
  };

  const handleCopyLink = () => {
    if (isProduct && !isLoggedIn) {
      onRequestLogin();
      return;
    }
    copyToClipboard();
  };

  // Les appelants passent tantôt un produit déjà mis en forme (`image` = URL
  // complètes, `name`), tantôt l'objet brut de l'API (`label`, `default_image`,
  // `images[]`, `commission` en chaîne) : on accepte les deux.
  const commission = parsePrice(product?.commission);
  const mappedImg = Array.isArray(product?.image) ? product.image[0] : product?.image;
  const productImg = mappedImg || getImageUrl(resolveProductImagePath(product));
  const productName = product?.name || product?.label || product?.title || "";
  const canShare = !isProduct || (isLoggedIn && !!shareUrl);

  const socials = [
    { key: "WhatsApp", Icon: WhatsAppIcon },
    { key: "Facebook", Icon: FacebookIcon },
    { key: "Messenger", Icon: MessengerIcon },
    { key: "Instagram", Icon: InstagramIcon },
  ];

  return (
    <div className="px-4 pb-4">
      {isProduct ? (
        <div className="flex items-center gap-3 bg-orange-50/60 border border-orange-100 rounded-2xl p-3">
          {productImg && (
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-white flex-shrink-0 border border-orange-100">
              <img src={productImg} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {productName && (
              <p className="text-sm font-semibold text-gray-800 truncate">{productName}</p>
            )}
            {commission > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Tu gagnes{" "}
                <span className="text-orange-500 font-bold">
                  {commission.toLocaleString("fr-FR")} FCFA
                </span>{" "}
                par vente
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-600 text-center leading-relaxed px-1">
          {shareCopy.description}
        </p>
      )}

      {error && <p className="text-red-500 text-xs text-center mt-2">{error}</p>}

      <p className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wide mt-4">
        Partager via
      </p>
      <div className="flex gap-3 justify-around mt-3 mb-6">
        {socials.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => handleShare(s.key)}
            className={`flex flex-col items-center gap-1.5 transition-opacity ${
              canShare || loading ? "opacity-100" : "opacity-40"
            }`}
          >
            <s.Icon />
            <span className="text-[11px] text-gray-500 font-medium">{s.key}</span>
          </button>
        ))}
      </div>

      {isProduct && !isLoggedIn ? (
        <div className="space-y-2 mt-2">
          <p className="text-center text-xs text-gray-400">
            Connecte-toi pour obtenir ton lien affilié
          </p>
          <ButtonCta onClick={onRequestLogin}>Connexion</ButtonCta>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleCopyLink}
          disabled={!shareUrl || loading}
          className="flex items-center justify-center gap-2 text-sm rounded-xl font-semibold w-full py-3 px-4 bg-gray-900 text-white disabled:opacity-40 hover:bg-gray-800 active:scale-[0.98] transition-all"
        >
          {copySuccess ? (
            <>
              <Check className="w-4 h-4 text-green-400" /> Lien copié !
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />{" "}
              {loading ? "Génération du lien…" : "Copier le lien"}
            </>
          )}
        </button>
      )}
    </div>
  );
}
